import { format, isValid, formatDistance, parseISO, addHours } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

import { ISiteVisit } from '@/types/dashboardContext.type';
import { LocalizationTypes } from '@/types/localization.type';

export type DateTimePattern =
  | 'dd/MM/yyyy'
  | 'do LLL yyyy'
  | 'd MMM yyyy'
  | 'do LLL yyyy HH:mm'
  | 'do LLL yyyy hh:mm a';

/**
 * Formats a given date string based on the specified pattern.
 * @param {string} date - The date string to be formatted.
 * @param {DateTimePattern} [pattern='dd/MM/yyyy'] - The pattern to format the date. Defaults to 'dd/MM/yyyy'.
 * @returns {string} - The formatted date string.
 *
 * Available date formatting patterns:
 *
 * Pattern: dd/MM/yyyy
 * Example: 30/08/2024
 *
 * Pattern: do LLL yyyy
 * Example: 30th Aug 2024
 */
export const formatDate = (
  date: string,
  pattern: DateTimePattern = 'do LLL yyyy',
  locale: LocalizationTypes = 'en'
): string => {
  return isValid(new Date(date))
    ? format(new Date(date), pattern, {
        locale: locale === 'ar' ? ar : enUS,
      })
    : 'Invalid Date';
};

/**
 * Return duration in readable locale string from milliseconds
 * @param {number} durationInMilliseconds - The milliseconds.
 * @param {string} [locale='ar'] - The locale we want to convert the duration. Defaults to 'ar'.
 *
 * Examples:
 *
 * durationInMilliseconds: 40000000
 * locale: en
 * output: about 11 hours
 *
 * durationInMilliseconds: 40000000
 * locale: ar
 * output: 11 ساعة تقريباً
 */
export const durationInLocaleString = (
  durationInMilliseconds: number,
  locale: string = 'ar'
): string => {
  return formatDistance(0, durationInMilliseconds, {
    locale: locale === 'ar' ? ar : enUS,
  });
};

export function isWithinTwoHoursFromGivenDate(SRCreatedDate: string): boolean {
  const createdTime = new Date(SRCreatedDate).getTime(); // Convert createdDate to timestamp
  const currentTime = Date.now(); // Get the current timestamp
  const twoHoursInMillis = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Check if the current time is within 2 hours of the created time
  return currentTime - createdTime <= twoHoursInMillis;
}

/* Converting System time zone into UAE time zone */
export function convertTimeZoneDetailPage(data: string | any) {
  const localDate = new Date(data);
  // Convert to Dubai time
  const uaeDateTime = new Date(
    localDate.toLocaleString('en-US', { timeZone: 'Asia/Dubai' })
  );

  // Get the current Dubai time
  const currentUaeTime = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' })
  );

  const isExpired: boolean = uaeDateTime > currentUaeTime;

  return { uaeDateTime, currentUaeTime, isExpired };
}

export function convertTimeZoneNotification(data: ISiteVisit | any) {
  const siteVisitDate: string = data?.siteVisitDate;
  const siteVisitTime: string = data?.siteVisitTime;
  const [day, month, year] = siteVisitDate.split('/').map(Number);
  const [time, period] = siteVisitTime.split(' ');
  const [hoursStr, minutes] = time.split(':').map(Number);

  let hours = hoursStr; // Keep 'hours' mutable

  if (period === 'PM' && hours !== 12) {
    hours += 12;
  } else if (period === 'AM' && hours === 12) {
    hours = 0;
  }
  // Create a Date object in the system's local timezone
  const localDate = new Date(year, month - 1, day, hours, minutes);
  // Get the current Dubai time
  const currentUaeTime = new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' })
  );

  const isExpired: boolean = localDate > currentUaeTime;

  return { uaeDateTime: localDate, currentUaeTime, isExpired };
}

export const formatDateTimeRange = (datetimeString: string): string => {
  try {
    const timeZone = 'Asia/Dubai';
    // Parse the date as UTC first, then format in Dubai time
    const parsedDate = parseISO(datetimeString); // Assume input is in UTC or known format
    const dubaiStart = new Date(
      parsedDate.toLocaleString('en-US', { timeZone })
    );
    const dubaiEnd = addHours(dubaiStart, 2);

    const getDayWithSuffix = (day: number) => {
      if (day > 3 && day < 21) return `${day}TH`;
      switch (day % 10) {
        case 1:
          return `${day}ST`;
        case 2:
          return `${day}ND`;
        case 3:
          return `${day}RD`;
        default:
          return `${day}TH`;
      }
    };

    const dayWithSuffix = getDayWithSuffix(parsedDate.getDate());
    const month = format(parsedDate, 'MMM').toUpperCase(); // e.g. JUL
    const year = format(parsedDate, 'yyyy');
    const startTime = format(dubaiStart, 'hh:mm a').toUpperCase();
    const formattedEndTime = format(dubaiEnd, 'hh:mm a').toUpperCase();

    return `${dayWithSuffix} ${month} ${year}  ${startTime} – ${formattedEndTime}`;
  } catch (error) {
    console.error('Invalid datetime input:', datetimeString, error);
    return '';
  }
};
